import React, { useEffect, useRef } from "react";
import styles from "./styles.module.scss";
import Portal from "app/components/portal";
import useModalFocus from "app/utils/hooks/useModalFocus";
import { ModalLayoutProps } from "./index.types";
import cls from "app/utils/classNames";

const ModalLayout = ({
  children,
  onClose,
  isOpen,
  classNames,
  centerVertically = false,
  wrapperClassName,
  'data-testid': testId,
  dialogLabel,
}: ModalLayoutProps) => {
  const popupRef = useRef<HTMLDivElement>(null);
  // Only dialogs (dialogLabel) manage focus; the legacy usages keep their
  // behavior.
  useModalFocus(Boolean(isOpen && dialogLabel), popupRef);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        // onClose is optional and 31 of the 126 usages leave it out, so calling it
        // unconditionally threw a TypeError on Escape for every one of them.
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <Portal>
      <div className={cls(styles.wrapper, {[styles.center_vertically_mobile]: centerVertically}, wrapperClassName)}>
        <div className={cls(styles.overlay)} onClick={onClose} />
        <div
          ref={popupRef}
          className={cls(styles.popup, classNames)}
          data-testid={testId}
          role={dialogLabel ? "dialog" : undefined}
          aria-modal={dialogLabel ? true : undefined}
          aria-label={dialogLabel}
          tabIndex={dialogLabel ? -1 : undefined}
        >
          {children}
        </div>
      </div>
    </Portal>
  );
};

export default ModalLayout;
